(() => {
  'use strict';

  const PRODUCT_CACHE = new Map();
  const COLOR_OPTION_NAMES = ['COLORE', 'COLOR', 'COLOUR', 'COULEUR', 'FARBE'];
  const SIZE_OPTION_NAMES = ['TAGLIA', 'SIZE', 'TAILLE', 'GRÖSSE', 'GROSSE'];
  let INSTANCE_COUNTER = 0;

  const normalize = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ')
    .trim();

  const slug = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const rootUrl = () => {
    const root = window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const productUrl = (handle, variantId, view) => {
    const url = new URL(`${rootUrl()}products/${encodeURIComponent(handle)}`, window.location.origin);
    if (variantId) url.searchParams.set('variant', String(variantId));
    if (view) url.searchParams.set('view', view);
    return `${url.pathname}${url.search}`;
  };

  const imageUrl = (source, width) => {
    if (!source) return '';
    const url = new URL(source, window.location.origin);
    if (width) url.searchParams.set('width', String(width));
    if (source.startsWith('//')) return `//${url.host}${url.pathname}${url.search}`;
    if (source.startsWith('/')) return `${url.pathname}${url.search}`;
    return `${url.origin}${url.pathname}${url.search}`;
  };

  const formatPrice = (cents) => {
    const amount = Number(cents || 0);
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(
        amount,
        window.theme?.settings?.money_with_currency_format || '€{{amount_with_comma_separator}}'
      );
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount / 100);
  };

  const getImage = (source) => {
    if (!source) return null;
    if (typeof source === 'string') return { src: source };
    if (source.preview_image?.src) return source.preview_image;
    if (source.src) return source;
    return null;
  };

  const getVariantImage = (variant) => getImage(variant?.featured_image)
    || getImage(variant?.featured_media)
    || null;

  const imageGroupFromAlt = (alt) => {
    const text = String(alt || '');
    if (!text.includes('#')) return '';
    return slug(text.split('#').pop());
  };

  const getTargetImage = (product, variant, targetColor) => {
    const expectedGroup = `colore_${slug(targetColor)}`;
    const matching = (product.media || [])
      .filter((media) => {
        const mediaType = media.media_type || media.type;
        return (!mediaType || mediaType === 'image') && imageGroupFromAlt(media.alt) === expectedGroup;
      })
      .map(getImage)
      .find(Boolean);

    return matching || getVariantImage(variant) || getImage(product.featured_image) || getImage(product.images?.[0]);
  };

  const findColorOptionIndex = (product, targetColor) => {
    const options = Array.isArray(product.options) ? product.options : [];
    const namedIndex = options.findIndex((option) => {
      const name = normalize(typeof option === 'string' ? option : option?.name);
      return COLOR_OPTION_NAMES.some((candidate) => name.includes(candidate));
    });
    if (namedIndex >= 0) return namedIndex;

    const target = normalize(targetColor);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const optionCount = Math.max(
      options.length,
      ...variants.map((variant) => Array.isArray(variant.options) ? variant.options.length : 0),
      0
    );

    for (let index = 0; index < optionCount; index += 1) {
      if (variants.some((variant) => normalize(variant.options?.[index]) === target)) return index;
    }
    return -1;
  };

  const getVariantsForColor = (product, targetColor) => {
    const colorIndex = findColorOptionIndex(product, targetColor);
    if (colorIndex < 0) return [];
    const target = normalize(targetColor);
    return (product.variants || []).filter((variant) => normalize(
      variant.options?.[colorIndex] ?? variant[`option${colorIndex + 1}`]
    ) === target);
  };

  const findDestinationVariant = (product, targetColor) => {
    const matching = getVariantsForColor(product, targetColor);
    return matching.find((variant) => variant.available && getVariantImage(variant))
      || matching.find((variant) => variant.available)
      || matching.find((variant) => getVariantImage(variant))
      || matching[0]
      || null;
  };

  const findSizeOptionIndex = (product, targetColor) => {
    const colorIndex = findColorOptionIndex(product, targetColor);
    const options = Array.isArray(product.options) ? product.options : [];
    const namedIndex = options.findIndex((option, index) => {
      if (index === colorIndex) return false;
      const name = normalize(typeof option === 'string' ? option : option?.name);
      return SIZE_OPTION_NAMES.some((candidate) => name.includes(candidate));
    });
    if (namedIndex >= 0) return namedIndex;

    const optionCount = Math.max(
      options.length,
      ...(product.variants || []).map((variant) => Array.isArray(variant.options) ? variant.options.length : 0),
      0
    );
    if (optionCount === 2 && colorIndex >= 0) return colorIndex === 0 ? 1 : 0;
    return -1;
  };

  const getSizeChoices = (product, targetColor) => {
    const variants = getVariantsForColor(product, targetColor);
    const sizeIndex = findSizeOptionIndex(product, targetColor);
    if (sizeIndex < 0) return { sizeIndex, choices: [], variants };

    const optionDefinition = product.options?.[sizeIndex];
    const declaredValues = Array.isArray(optionDefinition?.values) ? optionDefinition.values : [];
    const variantValues = variants.map((variant) => variant.options?.[sizeIndex] ?? variant[`option${sizeIndex + 1}`]);
    const values = (declaredValues.length ? declaredValues : variantValues)
      .filter((value, index, valuesList) => value && valuesList.findIndex((candidate) => normalize(candidate) === normalize(value)) === index)
      .filter((value) => variants.some((variant) => normalize(
        variant.options?.[sizeIndex] ?? variant[`option${sizeIndex + 1}`]
      ) === normalize(value)));

    const choices = values.map((value) => {
      const matching = variants.filter((variant) => normalize(
        variant.options?.[sizeIndex] ?? variant[`option${sizeIndex + 1}`]
      ) === normalize(value));
      const selectedVariant = matching.find((variant) => variant.available) || matching[0] || null;
      return { label: value, variant: selectedVariant, available: Boolean(selectedVariant?.available) };
    });

    return { sizeIndex, choices, variants };
  };

  const priceMarkup = (variant) => {
    const compareAtPrice = Number(variant?.compare_at_price || 0);
    const price = Number(variant?.price || 0);
    const discounted = compareAtPrice > price;
    return `
      <span class="price">
        ${discounted ? `<del><span class="amount">${escapeHtml(formatPrice(compareAtPrice))}</span></del>` : ''}
        <ins><span class="amount${discounted ? ' discounted' : ''}">${escapeHtml(formatPrice(price))}</span></ins>
      </span>
    `;
  };

  const fetchProduct = (handle) => {
    if (!PRODUCT_CACHE.has(handle)) {
      const request = fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Prodotto ${handle}: HTTP ${response.status}`);
        return response.json();
      }).catch((error) => {
        PRODUCT_CACHE.delete(handle);
        throw error;
      });
      PRODUCT_CACHE.set(handle, request);
    }
    return PRODUCT_CACHE.get(handle);
  };

  const runWithConcurrency = async (items, limit, worker) => {
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        await worker(items[index], index);
      }
    });
    await Promise.all(runners);
  };

  class ArazenCartItemQuickView extends HTMLElement {
    connectedCallback() {
      if (this.dataset.arazenReady === 'true') return;
      this.dataset.arazenReady = 'true';

      this.addEventListener('click', (event) => this.open(event));
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.open(event);
      });
    }

    async open(event) {
      event.preventDefault();

      if (this.classList.contains('loading')) return;

      const handle = this.dataset.productHandle;
      const variantId = this.dataset.variantId;
      const drawer = document.getElementById('Product-Drawer');
      const drawerContent = drawer?.querySelector('#Product-Drawer-Content');

      if (!handle || !variantId || !drawer || !drawerContent) return;

      this.classList.add('loading');
      this.setAttribute('aria-busy', 'true');

      try {
        const href = productUrl(handle, variantId, 'quick-view');
        const response = await fetch(href, { method: 'GET' });

        if (!response.ok) {
          throw new Error(`Quick View ${handle}: risposta HTTP ${response.status}`);
        }

        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const incoming = parsed.querySelector('#Product-Drawer-Content');

        if (!incoming) {
          throw new Error(`Quick View ${handle}: contenuto non trovato`);
        }

        drawerContent.innerHTML = incoming.innerHTML;

        drawerContent.querySelectorAll('script[src]').forEach((sourceScript) => {
          const script = document.createElement('script');
          script.src = sourceScript.src;
          document.head.appendChild(script);
        });

        window.setTimeout(() => {
          if (window.Shopify?.PaymentButton) {
            window.Shopify.PaymentButton.init();
          }
          if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        }, 300);

        document.body.classList.add('open-cc', 'open-quick-view');
        drawer.classList.add('active');
        drawer.querySelector('.side-panel-close')?.focus();

        window.setTimeout(() => {
          drawer.querySelector('.product-quick-images--container')?.classList.add('active');
        });

        document.dispatchEvent(new CustomEvent('quick-view:open', {
          detail: { productUrl: href, productHandle: handle }
        }));

        if (typeof window.addIdToRecentlyViewed === 'function') {
          window.addIdToRecentlyViewed(handle);
        }
      } catch (error) {
        console.error('[Arazen Occhiata veloce - carrello]', error);
      } finally {
        this.classList.remove('loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('arazen-cart-item-quick-view')) {
    customElements.define('arazen-cart-item-quick-view', ArazenCartItemQuickView);
  }

  class ArazenCartCompleteLook extends HTMLElement {
    connectedCallback() {
      if (this.dataset.arazenReady === 'true') return;
      this.dataset.arazenReady = 'true';
      this.instanceId = ++INSTANCE_COUNTER;
      this.track = this.querySelector('.arazen-cart-complete-look__track');
      this.viewport = this.querySelector('.arazen-cart-complete-look__viewport');
      this.status = this.querySelector('.arazen-cart-complete-look__status');
      this.dataScript = this.querySelector('[data-arazen-cart-complete-look-data]');
      this.data = this.readData();

      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.viewport?.addEventListener('scroll', () => {
        this.updateScrollable();
        this.updatePagination();
      }, { passive: true });

      window.addEventListener('resize', () => {
        this.updateScrollable();
        this.updatePagination();
      }, { passive: true });

      this.render();
    }

    readData() {
      try {
        return JSON.parse(this.dataScript?.textContent || '{}');
      } catch (error) {
        console.error('[Arazen Completa il look - carrello] Dati non validi', error);
        return {};
      }
    }

    getRelations() {
      const seen = new Set();
      return (this.data.relations || [])
        .sort((first, second) => {
          const sourceDifference = Number(first.sourceIndex || 0) - Number(second.sourceIndex || 0);
          return sourceDifference || Number(first.order || 0) - Number(second.order || 0);
        })
        .filter((relation) => {
          const key = `${normalize(relation.destinationHandle)}|${normalize(relation.destinationColor)}`;
          if (!relation.destinationHandle || !relation.destinationColor || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    }

    createSkeleton() {
      const item = document.createElement('li');
      item.className = 'arazen-cart-complete-look__slide';
      item.innerHTML = `
        <div class="arazen-cart-complete-look__skeleton" aria-hidden="true">
          <div class="arazen-cart-complete-look__skeleton-image"></div>
          <div class="arazen-cart-complete-look__skeleton-content">
            <div class="arazen-cart-complete-look__skeleton-line"></div>
            <div class="arazen-cart-complete-look__skeleton-line arazen-cart-complete-look__skeleton-line--short"></div>
          </div>
        </div>
      `;
      return item;
    }

    async render() {
      const relations = this.getRelations();
      if (!relations.length) {
        this.classList.remove('has-multiple-products');
        this.removePagination();
        this.hidden = true;
        return;
      }

      this.classList.toggle('has-multiple-products', relations.length > 1);
      this.track.replaceChildren();
      const slots = relations.map(() => this.createSkeleton());
      this.track.append(...slots);
      let rendered = 0;

      await runWithConcurrency(relations, 3, async (relation, index) => {
        try {
          const product = await fetchProduct(relation.destinationHandle);
          const variant = findDestinationVariant(product, relation.destinationColor);
          const image = getTargetImage(product, variant, relation.destinationColor);
          if (!variant || !image) throw new Error(`Associazione non renderizzabile: ${relation.destinationHandle}`);

          slots[index].replaceChildren(this.buildCard(product, variant, relation.destinationColor, image));
          rendered += 1;
        } catch (error) {
          slots[index].remove();
          console.warn('[Arazen Completa il look - carrello]', error);
        }
      });

      if (!rendered) {
        this.classList.remove('has-multiple-products');
        this.removePagination();
        this.hidden = true;
        this.track.replaceChildren();
        return;
      }

      this.classList.toggle('has-multiple-products', rendered > 1);
      this.hidden = false;
      this.status.textContent = `${rendered} prodotti abbinati disponibili`;

      this.viewport?.scrollTo({ left: 0, behavior: 'auto' });
      this.setupPagination(rendered);
      this.updateScrollable();
      this.updatePagination();
    }

    setupPagination(count) {
      this.removePagination();

      if (this.dataset.context !== 'drawer' || count <= 1 || !this.viewport) return;

      const pagination = document.createElement('div');
      pagination.className = 'arazen-cart-complete-look__pagination';
      pagination.setAttribute('role', 'group');
      pagination.setAttribute('aria-label', 'Navigazione prodotti complementari');

      Array.from({ length: count }, (_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'arazen-cart-complete-look__dot';
        button.setAttribute('aria-label', `Mostra prodotto complementare ${index + 1} di ${count}`);
        button.dataset.index = String(index);

        button.addEventListener('click', () => this.scrollToSlide(index));
        pagination.appendChild(button);
      });

      this.viewport.insertAdjacentElement('afterend', pagination);
      this.pagination = pagination;
    }

    removePagination() {
      this.pagination?.remove();
      this.pagination = null;
    }

    scrollToSlide(index) {
      if (!this.viewport || !this.track) return;

      const slides = Array.from(this.track.querySelectorAll('.arazen-cart-complete-look__slide'));
      const target = slides[index];
      if (!target) return;

      this.viewport.scrollTo({
        left: target.offsetLeft,
        behavior: 'smooth'
      });
    }

    updatePagination() {
      if (!this.pagination || !this.viewport || !this.track) return;

      window.requestAnimationFrame(() => {
        const slides = Array.from(this.track.querySelectorAll('.arazen-cart-complete-look__slide'));
        if (!slides.length) return;

        const currentLeft = this.viewport.scrollLeft;

        let activeIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - currentLeft);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            activeIndex = index;
          }
        });

        this.pagination.querySelectorAll('.arazen-cart-complete-look__dot').forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle('is-active', isActive);

          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      });
    }

    buildCard(product, variant, targetColor, image) {
      const card = document.createElement('article');
      card.className = 'arazen-cart-complete-look__card';
      const link = productUrl(product.handle, variant.id);
      const sizeData = getSizeChoices(product, targetColor);
      const hasSizes = sizeData.sizeIndex >= 0 && sizeData.choices.length > 0;
      const directVariant = hasSizes ? null : (sizeData.variants.find((candidate) => candidate.available) || variant);
      const purchasable = hasSizes
        ? sizeData.choices.some((choice) => choice.available)
        : Boolean(directVariant?.available);
      const sizeGroup = `ArazenCartLook-${this.instanceId}-${product.id}-${slug(targetColor)}`;
      const sizeLabel = this.dataset.sizeLabel || 'Taglia';
      const soldOutLabel = this.dataset.soldOutLabel || 'Esaurito';
      const addLabel = 'Aggiungi';

      const sizeOptions = hasSizes ? `
        <div class="variations arazen-cart-complete-look__variations">
          <fieldset class="product-form__input product-form__input--block arazen-cart-complete-look__size-field">
            <div class="form__label">${escapeHtml(sizeLabel)}: <span class="form__label__value" data-arazen-cart-size-value></span></div>
            ${sizeData.choices.map((choice, index) => {
              const optionId = `${sizeGroup}-${index}`;
              return `
                <input
                  type="radio"
                  id="${escapeHtml(optionId)}"
                  name="${escapeHtml(sizeGroup)}"
                  value="${escapeHtml(choice.variant?.id || '')}"
                  data-price="${escapeHtml(choice.variant?.price || 0)}"
                  data-compare-at-price="${escapeHtml(choice.variant?.compare_at_price || 0)}"
                  data-size-label="${escapeHtml(choice.label)}"
                  data-arazen-cart-size-option
                  ${choice.available ? '' : 'class="is-disabled" disabled aria-disabled="true"'}
                >
                <label for="${escapeHtml(optionId)}" title="${escapeHtml(choice.available ? choice.label : `${choice.label} - ${soldOutLabel}`)}"><span>${escapeHtml(choice.label)}</span></label>
              `;
            }).join('')}
          </fieldset>
        </div>
      ` : '';

      card.innerHTML = `
        <div class="arazen-cart-complete-look__summary">
          <div class="arazen-cart-complete-look__image" href="${escapeHtml(link)}" title="${escapeHtml(product.title)}">
            <img
              src="${escapeHtml(imageUrl(image.src, 260))}"
              alt="${escapeHtml(`${product.title} - ${targetColor}`)}"
              loading="lazy"
              decoding="async"
              width="260"
              height="325"
            >
          </div>
          <div class="arazen-cart-complete-look__info">
            ${this.dataset.showVendor === 'true' && product.vendor ? `<span class="arazen-cart-complete-look__vendor">${escapeHtml(product.vendor)}</span>` : ''}
            <a class="arazen-cart-complete-look__title" href="${escapeHtml(link)}">${escapeHtml(product.title)}</a>
            <div data-arazen-cart-price>${priceMarkup(variant)}</div>
            <span class="arazen-cart-complete-look__color">${escapeHtml(targetColor)}</span>
            <div class="arazen-cart-complete-look__quick-view-row">
              <arazen-cart-item-quick-view
                class="text-button arazen-cart-item-quick-view arazen-cart-complete-look__quick-view"
                role="button"
                tabindex="0"
                aria-label="Occhiata veloce: ${escapeHtml(product.title)}"
                data-product-handle="${escapeHtml(product.handle)}"
                data-variant-id="${escapeHtml(variant.id)}"
              >OCCHIATA VELOCE</arazen-cart-item-quick-view>
            </div>
          </div>
        </div>
        <div class="arazen-cart-complete-look__purchase">
          ${sizeOptions}
          <form class="arazen-cart-complete-look__form" data-arazen-cart-add-form data-product-handle="${escapeHtml(product.handle)}" novalidate>
            <input type="hidden" name="id" value="${escapeHtml(directVariant?.available ? directVariant.id : '')}">
            <div class="arazen-cart-complete-look__add-row">
              <quantity-selector class="quantity arazen-cart-complete-look__quantity">
                <button class="minus" type="button" aria-label="Diminuisci quantità"><span aria-hidden="true">−</span></button>
                <input class="qty" type="number" name="quantity" data-arazen-cart-quantity value="1" min="1" step="1" inputmode="numeric" aria-label="Quantità">
                <button class="plus" type="button" aria-label="Aumenta quantità"><span aria-hidden="true">+</span></button>
              </quantity-selector>
              <button type="submit" class="button arazen-cart-complete-look__add-button" ${purchasable && !hasSizes ? '' : 'disabled'} aria-disabled="${purchasable && !hasSizes ? 'false' : 'true'}">
                <span class="arazen-cart-complete-look__add-text">${escapeHtml(purchasable ? addLabel : soldOutLabel)}</span>
                <span class="arazen-cart-complete-look__spinner" aria-hidden="true"></span>
              </button>
            </div>
            <p class="arazen-cart-complete-look__error" role="alert" hidden></p>
          </form>
        </div>
      `;
      return card;
    }

    onChange(event) {
      if (event.target.matches('[data-arazen-cart-quantity]')) {
        event.stopPropagation();
        return;
      }

      const option = event.target.closest('[data-arazen-cart-size-option]');
      if (!option || !option.checked || !this.contains(option)) return;

      const card = option.closest('.arazen-cart-complete-look__card');
      const form = card?.querySelector('[data-arazen-cart-add-form]');
      const idInput = form?.querySelector('[name="id"]');
      const button = form?.querySelector('.arazen-cart-complete-look__add-button');
      const selectedLabel = card?.querySelector('[data-arazen-cart-size-value]');
      const quickView = card?.querySelector('.arazen-cart-complete-look__quick-view');
      const variantId = option.value || '';
      if (!form || !idInput || !button) return;

      if (selectedLabel) selectedLabel.textContent = option.dataset.sizeLabel || '';
      if (quickView && variantId) quickView.dataset.variantId = variantId;
      idInput.value = variantId;
      button.disabled = !variantId;
      button.setAttribute('aria-disabled', variantId ? 'false' : 'true');

      if (!variantId) return;
      const link = productUrl(form.dataset.productHandle, variantId);
      
      card
        .querySelector('.arazen-cart-complete-look__title')
        ?.setAttribute('href', link);

      const priceRoot = card.querySelector('[data-arazen-cart-price]');
      if (priceRoot) {
        priceRoot.innerHTML = priceMarkup({
          price: option.dataset.price,
          compare_at_price: option.dataset.compareAtPrice
        });
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-arazen-cart-add-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const idInput = form.querySelector('[name="id"]');
      const quantityInput = form.querySelector('[name="quantity"]');
      const button = form.querySelector('.arazen-cart-complete-look__add-button');
      const error = form.querySelector('.arazen-cart-complete-look__error');
      const variantId = idInput?.value;
      const quantity = Math.max(Number.parseInt(quantityInput?.value, 10) || 1, 1);

      if (!variantId) {
        form.closest('.arazen-cart-complete-look__card')?.querySelector('[data-arazen-cart-size-option]:not(:disabled)')?.focus();
        return;
      }
      if (!button || button.classList.contains('loading')) return;

      error?.setAttribute('hidden', '');
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', String(quantity));
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(window.theme?.routes?.cart_add_url || `${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Impossibile aggiungere il prodotto al carrello.');

        this.renderCartContents(result);
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: result.items?.[0] || result } }));
      } catch (submitError) {
        if (error) {
          error.textContent = submitError.message;
          error.removeAttribute('hidden');
        }
        console.error('[Arazen Completa il look - carrello]', submitError);
      } finally {
        button.classList.remove('loading');
        button.disabled = !idInput.value;
        button.setAttribute('aria-disabled', idInput.value ? 'false' : 'true');
      }
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    getSectionInnerHTML(html, selector) {
      if (!html) return '';
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
    }

    renderCartContents(result) {
      this.getSectionsToRender().forEach((section) => {
        const root = document.getElementById(section.id);
        const sectionHtml = result.sections?.[section.section];
        if (!root || !sectionHtml) return;
        const replacement = root.querySelector(section.selector) || root;
        replacement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);

        if (section.id === 'Cart-Drawer') {
          root.notesToggle?.();
          root.removeProductEvent?.();
        }
        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(result);
        }
      });

      const drawer = document.getElementById('Cart-Drawer');
      if (drawer) {
        drawer.classList.add('active');
        document.body.classList.add('open-cart', 'open-cc');
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }
    }

    updateScrollable() {
      if (!this.viewport || !this.track) return;
      window.requestAnimationFrame(() => {
        this.track.classList.toggle('is-scrollable', this.viewport.scrollWidth - this.viewport.clientWidth > 2);
      });
    }
  }

  if (!customElements.get('arazen-cart-complete-look')) {
    customElements.define('arazen-cart-complete-look', ArazenCartCompleteLook);
  }
})();
